import {v4 as uuid} from 'uuid';
import {Syncer} from './sync';

let storage = window.localStorage


function get(object, key, default_value) {
    var result = object[key];
    return (typeof result !== "undefined") ? result : default_value;
}

function _double_sort(dict_of_entries) {
    // Sort by object key and entry names
    const keys = Object.keys(dict_of_entries).sort()
    const ordered = keys.reduce(
        (obj, key) => {
            obj[key] = dict_of_entries[key].sort((a, b) => a.name > b.name)
            return obj
        },
        {}
    );
    return ordered
}


export class Entry {
    constructor(name=null, category=null, section=null, description="", aliases=[]) {
        this.name = name
        this.category = category
        this.section = section
        this.description = description
        this.aliases = aliases
    }

    static from_data(data) {
        var entry = new Entry(
            get(data, "name", null),
            get(data, "category", null),
            get(data, "section", null),
            get(data, "description", ""),
            get(data, "aliases", [])
        )

        if ("id" in data) { entry.id = data.id }
        return entry
    }

    static by_category(entries) {
        let retval = {}
        for (const entry of entries) {
            if (!(entry.category in retval)) {
                retval[entry.category] = []
            }
            retval[entry.category].push(entry)
        }

        return _double_sort(retval)
    }

    static by_section(entries) {
        let retval = {}
        for (const entry of entries) {
            let section = entry.section
            if (section == undefined){
                // Make sure it is sorted at the front
                section = "0000"
            }
            if (!(section in retval)) {
                retval[section] = []
            }
            retval[section].push(entry)
        }

        return _double_sort(retval)
    }
}


export class Notebook {
    constructor(name, entries={}) {
        this.id = uuid()
        this.name = name
        this.entries = entries
    }

    get names_to_id() {
        let retval = {}
        for (const entry of Object.values(this.entries)) {
            retval[entry.name] = entry.id
            for (const alias of entry.aliases) {
                retval[alias] = entry.id
            }
        }

        return retval
    }

    get organized_entries() {
        // Return entries organized by section, and by category within each section
        let by_section = Entry.by_section(Object.values(this.entries))
        let retval = {}
        for (const [section_name, entries] of Object.entries(by_section)) {
            retval[section_name] = Entry.by_category(entries)
        }
        return retval
    }

    get entries_by_category() {
        return Entry.by_category(Object.values(this.entries))
    }

    find_references(entry) {
        let references = []
        for (const [ref_entry_id, ref_entry] of Object.entries(this.entries)) {
            if (ref_entry_id == entry.id) {
                continue
            }

            const names = [entry.name].concat(entry.aliases)

            for (const name of names) {
                const regex = new RegExp(`([\\W_])(${name})([\\W_])`, 'g')
                if (!references.includes(ref_entry) && ref_entry.description.match(regex) != null) {
                    references.push(ref_entry)
                }
            }
        }
        return references
    }

    upsert_entry(entry) {
        if (!("id" in entry)) {
            entry["id"] = uuid()
        }

        this.entries[entry.id] = entry
    }

    delete_entry(entry_id) {
        return delete this.entries[entry_id]
    }

    save() {
        storage.setItem(this.id, JSON.stringify(this))
    }

    delete() {
        storage.removeItem(this.id)
    }

    static load(notebook_id) {
        let serial = storage.getItem(notebook_id)
        if (serial === null) {
            return null
        }
        let data = JSON.parse(serial)
        let entries = Object.fromEntries(Object.entries(data.entries).map(
            ([id, stuff]) => [id, Entry.from_data(stuff)])
        )
        let retval = new Notebook(data.name, entries)
        retval.id = data.id
        if (retval.id == undefined) {
            return null
        }
        return retval
    }
}

export function list_notebooks() {
    let notebooks = []
    for (var i = 0 ; i < storage.length ; i++) {
        if (storage.key(i) == "syncUri" || storage.key(i) == "syncIdentifier") {
            continue
        }
        let notebook = Notebook.load(storage.key(i))
        if (notebook != null) {
            notebooks.push(notebook)
        }
    }
    return notebooks
}

export function export_all() {
    let data = []
    for (var i = 0 ; i < storage.length ; i++) {
        if (storage.key(i) == "syncUri" || storage.key(i) == "syncIdentifier") {
            continue
        }
        let notebook = Notebook.load(storage.key(i))
        if (notebook != null) {
            data.push(notebook)
        }
    }
    return data
}

export function import_all(json_data) {
    let data = JSON.parse(json_data)
    if (Array.isArray(data)) {
        // New style
        for (var i = 0 ; i < data.length ; i++) {
            let notebook_data = data[i]
            let notebook = new Notebook(notebook_data.name, notebook_data.entries)
            notebook.id = notebook_data.id
            notebook.save()
        }
    }
    else {
        // Old Style
        for (const name of Object.keys(data)) {
            let notebook = new Notebook(name, data[name]['entries'])
            notebook.save()
        }
    }
}
